import { useEffect, useState, useCallback } from 'react'
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Grid,
  HStack,
  Input,
  Select,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { apiClient } from '@/lib/api/client'

export type FunctionFormProps = {
  functionId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

const initialValues = {
  parent_id: '' as string | number,
  name: '',
  slug: '',
  descriptions: '',
  active: true,
}

function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
}

/**
 * Function form (e.g. "Printing", "Scanning" under category "Printer").
 * Maps to the "Function" dropdown in CreateAssetModal, which is filtered
 * by the asset's selected Category (machine type).
 */
export function FunctionForm({ functionId, onSuccess, onCancel }: FunctionFormProps) {
  const { showError, showSuccess } = useToastNotification()
  const [loading, setLoading] = useState(!!functionId)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string[]>>({})
  const [form, setForm] = useState(initialValues)
  const [slugTouched, setSlugTouched] = useState(false)
  const [categoryOptions, setCategoryOptions] = useState<Array<{ id: number; name: string }>>([])
  const [categoriesLoading, setCategoriesLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setCategoriesLoading(true)
    apiClient
      .get('/master-data/categories', { params: { per_page: 500 } })
      .then((res) => {
        if (cancelled) return
        const rows = Array.isArray(res.data?.data) ? res.data.data : []
        setCategoryOptions(rows.map((r: { id: number; name?: string }) => ({ id: Number(r.id), name: String(r.name ?? r.id) })))
      })
      .catch(() => {
        if (!cancelled) setCategoryOptions([])
      })
      .finally(() => {
        if (!cancelled) setCategoriesLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const loadOne = useCallback(async () => {
    if (!functionId) return
    setLoading(true)
    setErrors({})
    try {
      const { data } = await apiClient.get(`/master-data/functions/${functionId}`)
      const r = data?.data ?? data
      setForm({
        parent_id: r.parent_id ?? '',
        name: r.name ?? '',
        slug: r.slug ?? '',
        descriptions: r.descriptions ?? '',
        active: r.active !== false,
      })
      setSlugTouched(true)
    } catch (e: unknown) {
      showError(e as Error)
    } finally {
      setLoading(false)
    }
  }, [functionId, showError])

  useEffect(() => {
    if (functionId) {
      loadOne()
    } else {
      setForm(initialValues)
      setSlugTouched(false)
    }
  }, [functionId, loadOne])

  const updateField = (field: keyof typeof form, value: string | number | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }

  const handleNameChange = (value: string) => {
    setForm((prev) => ({
      ...prev,
      name: value,
      slug: slugTouched ? prev.slug : slugify(value),
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})
    setSaving(true)
    try {
      const payload = {
        parent_id: form.parent_id === '' ? null : Number(form.parent_id),
        name: form.name.trim(),
        slug: (form.slug.trim() || slugify(form.name)).trim(),
        descriptions: form.descriptions.trim() || null,
        active: form.active,
      }
      if (functionId) {
        await apiClient.put(`/master-data/functions/${functionId}`, payload)
        showSuccess('Success', 'Function updated successfully')
      } else {
        await apiClient.post('/master-data/functions', payload)
        showSuccess('Success', 'Function created successfully')
      }
      onSuccess?.()
    } catch (err: unknown) {
      const res = (err as { response?: { data?: { errors?: Record<string, string[]> } } })?.response?.data
      if (res?.errors && typeof res.errors === 'object') {
        setErrors(res.errors)
      } else {
        showError(err as Error)
      }
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Box py={8}>
        <Box as="span">Loading...</Box>
      </Box>
    )
  }

  return (
    <Box as="form" onSubmit={handleSubmit}>
      <VStack spacing={4} align="stretch">
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Category (Machine Type) <Box as="span" color="red.500">*</Box>
          </FormLabel>
          <FormControl isInvalid={!!errors.parent_id?.length} isRequired>
            <Select
              value={form.parent_id}
              onChange={(e) => updateField('parent_id', e.target.value)}
              placeholder={categoriesLoading ? 'Loading...' : 'Select category'}
              isDisabled={categoriesLoading}
            >
              {categoryOptions.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </Select>
            <FormHelperText>The machine type this function applies to (e.g. Printer, POS Terminal).</FormHelperText>
            <FormErrorMessage>{errors.parent_id?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Name <Box as="span" color="red.500">*</Box>
          </FormLabel>
          <FormControl isInvalid={!!errors.name?.length} isRequired>
            <Input
              value={form.name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="e.g. Printing, Scanning, Payment Processing"
            />
            <FormErrorMessage>{errors.name?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Slug <Box as="span" color="red.500">*</Box>
          </FormLabel>
          <FormControl isInvalid={!!errors.slug?.length} isRequired>
            <Input
              value={form.slug}
              onChange={(e) => {
                setSlugTouched(true)
                updateField('slug', slugify(e.target.value))
              }}
              placeholder="auto-generated from name"
            />
            <FormErrorMessage>{errors.slug?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Description</FormLabel>
          <FormControl isInvalid={!!errors.descriptions?.length}>
            <Textarea
              value={form.descriptions}
              onChange={(e) => updateField('descriptions', e.target.value)}
              placeholder="Optional description"
              rows={3}
            />
            <FormErrorMessage>{errors.descriptions?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="center">
          <FormLabel pt={2} fontWeight="medium" mb={0}>Active</FormLabel>
          <FormControl>
            <Checkbox
              isChecked={form.active}
              onChange={(e) => updateField('active', e.target.checked)}
              colorScheme="blue"
            >
              <Box as="span">Available for selection on store assets.</Box>
            </Checkbox>
          </FormControl>
        </Grid>

        <HStack spacing={4} justify="flex-end" pt={4}>
          {onCancel && (
            <Button variant="outline" onClick={onCancel} isDisabled={saving}>
              Cancel
            </Button>
          )}
          <Button type="submit" colorScheme="blue" isLoading={saving} loadingText={functionId ? 'Saving...' : 'Creating...'}>
            {functionId ? 'Save' : 'Create Function'}
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}
